import { useCallback } from "react";

import {
	BillingTypeTarget,
	BillingTypeTermValidation,
	CurrencyType,
	type AdditionalFeeItemInput,
} from "@/lib/gql/__generated__/graphql";
import { getUUID } from "@/lib/uuid/get-uuid";

import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";

import {
	DEFAULT_ADDITIONAL_FEE,
	billingTypeTargetOptions,
	billingTypeTermOptions,
	billingTypeTermValidationOptions,
	kidsAgeOptions,
} from "./constants";

const inputItem = ({
	item,
	itemIndex,
	targetIndex,
	key,
	input,
}: {
	item: ForAutoGenerator<AdditionalFeeItemInput, "">;
	itemIndex: number;

	targetIndex: number;
	key:
		| "name"
		| "isNotDiscountTarget"
		| "isNotTargetOnDayTrip"
		| "isExcludeCancelFeeTarget"
		| "isExcludedConsumptionTax"
		| "billingTypeTarget"
		| "billingTypeTerm"
		| "billingTypeTermValidation"
		| "termValidationApplyStartDt"
		| "termValidationApplyEndDt"
		| "useKidsPrice";
	input: string | boolean | undefined;
}) => {
	if (itemIndex === targetIndex) {
		return {
			...item,
			[key]: {
				isEdited: true,
				value: input,
			},
		};
	}
	return item;
};

type Props = {
	additionalFees: ForArrayType<AdditionalFeeItemInput[]>;

	onSetViewModel: (values: { key: "additionalFees"; value: ForArrayType<AdditionalFeeItemInput[]> }[]) => void;
};

export const useAdditionalFee = ({ additionalFees, onSetViewModel }: Props) => {
	const isEdited = additionalFees.isEdited;
	const hasError = additionalFees.hasError;

	const append = useCallback(() => {
		onSetViewModel([
			{
				key: "additionalFees",
				value: {
					...additionalFees,
					value: [...additionalFees.value, DEFAULT_ADDITIONAL_FEE(getUUID())],
				},
			},
		]);
	}, [additionalFees, onSetViewModel]);

	const remove = useCallback(
		(index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.filter((_, i) => i !== index),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetName = useCallback(
		(input: string, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "name",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetBillingTypeTarget = useCallback(
		(input: string, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "billingTypeTarget",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const shouldShowKidsPriceSettings = useCallback(
		(index: number) => {
			const item = additionalFees.value[index];
			const billingTypeTarget = item.billingTypeTarget.value;

			return billingTypeTarget === BillingTypeTarget.Person;
		},
		[additionalFees],
	);

	const onSetBillingTypeTermValidationOptions = useCallback(
		(input: string, index: number) => {
			const value = input as BillingTypeTermValidation;

			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							const newItem = inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "billingTypeTermValidation",
								input: value,
							});

							return {
								...newItem,
								termValidationApplyStartDt:
									value === BillingTypeTermValidation.LimitedAnnually
										? {
												...item.termValidationApplyStartDt,
												isEdited: true,
												value: "",
											}
										: undefined,
								termValidationApplyEndDt:
									value === BillingTypeTermValidation.LimitedAnnually
										? {
												...item.termValidationApplyEndDt,
												isEdited: true,
												value: "",
											}
										: undefined,
							};
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetKidsMaxAge = useCallback(
		(input: string, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							if (i === index) {
								const target = item.kidsPrices?.value?.at(0); // onSetUseKidsPrice() で kidsPrices が生成されることを前提としたコード
								if (!target) return item;

								return {
									...item,
									kidsPrices: {
										isEdited: true,
										hasError: false,
										value: [
											{
												...target,
												kidsMaxAge: {
													isEdited: true,
													value: Number(input),
												},
											},
										],
									},
								};
							} else {
								return item;
							}
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetKidsPriceAmount = useCallback(
		(input: number | undefined, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							if (i === index) {
								const target = item.kidsPrices?.value?.at(0); // onSetUseKidsPrice() で kidsPrices が生成されることを前提としたコード
								if (!target) return item;

								return {
									...item,
									kidsPrices: {
										isEdited: true,
										hasError: false,
										value: [
											{
												...target,
												price: {
													...target.price,
													amount: {
														isEdited: true,
														value: input!,
													},
												},
											},
										],
									},
								};
							} else {
								return item;
							}
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const shouldShowTermValidationSettings = (index: number) => {
		const item = additionalFees.value[index];
		const billingTypeTermValidation = item.billingTypeTermValidation?.value;

		return billingTypeTermValidation === BillingTypeTermValidation.LimitedAnnually;
	};

	const onSetUseKidsPrice = useCallback(
		(input: boolean, index: number) => {
			if (input) {
				onSetViewModel([
					{
						key: "additionalFees",
						value: {
							...additionalFees,
							value: additionalFees.value.map((item, i) => {
								if (i === index) {
									return {
										...item,
										useKidsPrice: {
											isEdited: input,
											value: input,
										},
										kidsPrices: {
											isEdited: true,
											hasError: false,
											value: [
												{
													kidsMaxAge: {
														isEdited: false,
														value: 0,
													},
													price: {
														amount: {
															isEdited: false,
															value: 0,
														},
														currencyType: {
															isEdited: false,
															value: CurrencyType.Jpy,
														},
													},
												},
											],
										} satisfies ForAutoGenerator<AdditionalFeeItemInput, "">["kidsPrices"],
									} satisfies ForAutoGenerator<AdditionalFeeItemInput, "">;
								} else {
									return item;
								}
							}),
						},
					},
				]);
			} else {
				onSetViewModel([
					{
						key: "additionalFees",
						value: {
							...additionalFees,
							value: additionalFees.value.map((item, i) => {
								if (i === index) {
									return {
										...item,
										useKidsPrice: {
											isEdited: input,
											value: input,
										},
										kidsPrices: {
											isEdited: true,
											hasError: false,
											value: [],
										} satisfies ForAutoGenerator<AdditionalFeeItemInput, "">["kidsPrices"],
									} satisfies ForAutoGenerator<AdditionalFeeItemInput, "">;
								} else {
									return item;
								}
							}),
						},
					},
				]);
			}
		},
		[additionalFees, onSetViewModel],
	);

	const onSetApplicableTerm = useCallback(
		(input: DateRange, index: number) => {
			{
				onSetViewModel([
					{
						key: "additionalFees",
						value: {
							...additionalFees,
							value: additionalFees.value.map((item, i) => {
								if (i === index) {
									return {
										...item,
										termValidationApplyStartDt: {
											isEdited: true,
											value: input.from,
										},
										termValidationApplyEndDt: {
											isEdited: true,
											value: input.to,
										},
									};
								} else {
									return item;
								}
							}),
						},
					},
				]);
			}
		},
		[additionalFees, onSetViewModel],
	);

	const onSetBillingTypeTerm = useCallback(
		(input: string, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "billingTypeTerm",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetPriceAmount = useCallback(
		(input: number | undefined, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							if (i === index) {
								return {
									...item,
									price: {
										...item.price,
										amount: {
											isEdited: true,
											value: input!,
										},
									},
								};
							} else {
								return item;
							}
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetIsNotDiscountTarget = useCallback(
		(input: boolean, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "isNotDiscountTarget",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetIsNotTargetOnDayTrip = useCallback(
		(input: boolean, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "isNotTargetOnDayTrip",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetIsExcludeCancelFeeTarget = useCallback(
		(input: boolean, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "isExcludeCancelFeeTarget",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	const onSetIsExcludedConsumptionTax = useCallback(
		(input: boolean, index: number) => {
			onSetViewModel([
				{
					key: "additionalFees",
					value: {
						...additionalFees,
						value: additionalFees.value.map((item, i) => {
							return inputItem({
								item,
								itemIndex: i,
								targetIndex: index,
								key: "isExcludedConsumptionTax",
								input,
							});
						}),
					},
				},
			]);
		},
		[additionalFees, onSetViewModel],
	);

	return {
		isEdited,
		hasError,

		additionalFees,

		onSetName,

		billingTypeTargetOptions,
		onSetBillingTypeTarget,

		billingTypeTermOptions,
		onSetBillingTypeTerm,

		billingTypeTermValidationOptions,
		onSetBillingTypeTermValidationOptions,
		shouldShowTermValidationSettings,

		onSetApplicableTerm,

		onSetUseKidsPrice,
		kidsAgeOptions,
		onSetKidsMaxAge,
		onSetKidsPriceAmount,

		onSetPriceAmount,

		shouldShowKidsPriceSettings,

		onSetIsNotDiscountTarget,
		onSetIsNotTargetOnDayTrip,
		onSetIsExcludeCancelFeeTarget,
		onSetIsExcludedConsumptionTax,

		append,
		remove,
	};
};
